import React, { useState } from 'react';
import { Button, message } from 'antd';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_BASE_URL } from '../../../../constants/Constants';

const PDFReport = ({supplierId}) => {
  const [loading, setLoading] = useState(false);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const authToken = sessionStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      };

      const [configResponse, answersResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/report`, { headers }),
        fetch(`${API_BASE_URL}/reportsBySupplier/data/${supplierId}`, { headers }),
      ]);

      if (!configResponse.ok) throw new Error('Failed to fetch configuration data');
      if (!answersResponse.ok) {
        if (answersResponse.status === 404) {
          message.warning('Data not available for this supplier');
          return;
        }
        throw new Error('Failed to fetch answers data');
      }

      const configData = await configResponse.json();
      const answersData = await answersResponse.json();

      await generatePDF(configData, answersData);
    } catch (err) {
      console.error('Error fetching data:', err);
      message.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return typeof num === 'number' ? num.toFixed(2) : num;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const groupQuestionResponses = (responses, questionId) => {
    if (!responses) return [];
    return responses.filter(response => 
      response.questionId === questionId || 
      response.questionId.startsWith(`${questionId}_duplicate_`)
    );
  };

  const addFooter = (doc, pageWidth, pageHeight) => {
    doc.setFontSize(10);
    doc.setTextColor(74, 85, 104);
    const footer = `© ${new Date().getFullYear()} Sustainability Report - Generated automatically`;
    doc.text(footer, pageWidth / 2, pageHeight - 20, { align: 'center' });
  };

  const hasValidResponses = (pillarAnswers) => {
    return pillarAnswers && 
           pillarAnswers.actualResponses && 
           pillarAnswers.actualResponses.length > 0 &&
           pillarAnswers.actualResponses.some(response => 
             response.responses && 
             response.responses.length > 0
           );
  };

  const generatePDF = async (configData, answersData) => {
    if (!configData) return;
    
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const bottomMargin = 15;
      const contentWidth = pageWidth - (2 * margin);
      const bulletIndent = 10;

      // Header
      doc.setFontSize(24);
      doc.setTextColor(26, 54, 93);
      doc.text('Sustainability Report', margin, yPos);
      
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(74, 85, 104);
      doc.text(`Generated on: ${formatDate(new Date())}`, margin, yPos);
      
      yPos += 20;

      const pillarsWithData = configData.filter(pillarConfig => 
        hasValidResponses(answersData.find(a => a.pillarId === pillarConfig.pillarId))
      );

      if (pillarsWithData.length === 0) {
        doc.setFontSize(14);
        doc.setTextColor(31, 41, 55);
        doc.text('No data reported', margin, yPos);
        
        addFooter(doc, pageWidth, pageHeight);
        doc.save(`sustainability-report-${new Date().toISOString().split('T')[0]}.pdf`);
        return;
      }

      pillarsWithData?.forEach((pillarConfig, pIndex) => {
        const pillarAnswers = answersData.find(a => a.pillarId === pillarConfig.pillarId);
        
        if (yPos > pageHeight - bottomMargin) {
          doc.addPage();
          yPos = 20;
        }

        // Pillar title
        doc.setFillColor(247, 250, 252);
        doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
        doc.setFontSize(16);
        doc.setTextColor(37, 99, 235);
        doc.text(pillarConfig.pillarId, margin, yPos);
        yPos += 15;

        pillarConfig?.reportQuestions?.forEach((question, qIndex) => {
          const allResponses = groupQuestionResponses(pillarAnswers.actualResponses, question.questionId);

          if (!allResponses || allResponses.length === 0 || 
              !allResponses.some(response => response.responses && response.responses.length > 0)) {
            return;
          }

          if (yPos > pageHeight - bottomMargin) {
            doc.addPage();
            yPos = 20;
          }

          const questionNumber = qIndex + 1;

          doc.setFontSize(12);
          doc.setTextColor(31, 41, 55);
          const questionText = question.type === 'TABLE' ? question.text : question.question;
          doc.text(`${questionNumber}. ${questionText}`, margin, yPos);
          yPos += 9;

          if (question.type === 'QUESTION') {
            allResponses?.forEach((response) => {
              if (!response.isSelected) return;

              let responseText = question.text;
              response.responses[0].response?.forEach(field => {
                responseText = responseText.replace(`<${field.fieldId}>`, field.value);
              });

              doc.setFontSize(12);
              doc.setTextColor(55, 65, 81);
              
              doc.text('•', margin + 5, yPos);
              const splitText = doc.splitTextToSize(responseText, contentWidth - bulletIndent - 5);
              splitText?.forEach(line => {
                if (yPos > pageHeight - bottomMargin) {
                  doc.addPage();
                  yPos = 20;
                }
                doc.text(line, margin + bulletIndent, yPos);
                yPos += 7;
              });

              if (response.totalEmissionSaved) {
                if (yPos > pageHeight - bottomMargin) {
                  doc.addPage();
                  yPos = 20;
                }
                doc.setFontSize(10);
                doc.setTextColor(5, 150, 105);
                doc.setFont(undefined, 'bold')
                doc.text('Emission Saved: ', margin + 10, yPos);
                doc.setFont(undefined,'normal')
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(9.5);
                doc.text(`${formatNumber(parseFloat(response.totalEmissionSaved))} MT CO2e`, margin + 40, yPos);
                doc.setFontSize(undefined, 'normal');
                yPos += 10;
              }
            });
          } else if (question.type === 'TABLE') {
            const tableData = [];
            const tableHeaders = question.inputFields.map(field => field.tableHeader || field.label);
            
            allResponses?.forEach(response => {
              if (response.responses && response.responses.length > 0) {
                response.responses?.forEach(rowResponse => {
                  const row = question.inputFields.map(field => {
                    const fieldResponse = rowResponse.response.find(r => r.fieldId === field.inputId);
                    return fieldResponse ? fieldResponse.value : '';
                  });
                  tableData.push(row);
                });
              }
            });

            if (tableData.length > 0) {
              const remainingSpace = pageHeight - yPos - bottomMargin;
              if (remainingSpace < 40) {
                doc.addPage();
                yPos = 20;
              }

              doc.autoTable({
                head: [tableHeaders],
                body: tableData,
                startY: yPos,
                margin: { left: margin },
                styles: { 
                  fontSize: 8,
                  cellPadding: 3,
                  halign: 'center'
                },
                headStyles: { 
                  fillColor: [243, 244, 246], 
                  textColor: [0, 0, 0],
                  fontStyle: 'bold',
                  halign: 'center'
                },
                bodyStyles: {
                  fillColor: [255, 255, 255]
                },
                columnStyles: {
                  0: { halign: 'left' },
                  2: { halign: 'center' },
                  3: { halign: 'center' },
                  4: { halign: 'center' },
                  5: { halign: 'center' }
                }
              });
              
              yPos = doc.lastAutoTable.finalY + 10;
            }
          }

          yPos += 10;
        });

        if (pIndex === pillarsWithData.length - 1) {
          addFooter(doc, pageWidth, pageHeight);
        }
      });

      doc.save(`sustainability-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      message.error('Failed to generate PDF report');
    }
  };

  return (
    <Button
      onClick={fetchData}
      loading={loading}
      className="bg-[#1F405A] text-white hover:bg-[#1e3a54]"
    >
      Download Report
    </Button>
  );
};

export default PDFReport;